/**
 * Cross-sprint field update handlers.
 * 
 * All follow the same pattern:
 * - If task in current sprint → delegate to actions.handleX
 * - If task in nested sprint → update sprint directly
 */

const LOG_PREFIX = '[useCrossSprintActions]';

/**
 * Creates a simple field update handler.
 * Used for rename, icon, priority, dueDate, etc.
 */
const createFieldHandler = ({
  findSprintForTask,
  localActionHandler,
  fieldName,
  onUpdateSprint,
  handlerName,
}) => (taskId, value) => {
  const { sprint: targetSprint, isCurrentSprint } = findSprintForTask(taskId);

  if (!targetSprint) {
    console.warn(`${LOG_PREFIX} ${handlerName}: Task not found in any sprint:`, taskId);
    return;
  }

  if (isCurrentSprint) {
    localActionHandler(taskId, value);
  } else {
    const updatedSprint = {
      ...targetSprint,
      tasks: targetSprint.tasks.map(t =>
        t.id === taskId
          ? { ...t, [fieldName]: value, updatedAt: new Date().toISOString() }
          : t
      ),
    };
    onUpdateSprint?.(updatedSprint);
  }
};

/**
 * Creates all field update handlers.
 */
export const createFieldHandlers = ({
  findSprintForTask,
  actions,
  modals,
  onUpdateSprint,
}) => ({
  handleCrossSprintRename: createFieldHandler({
    findSprintForTask,
    localActionHandler: actions.handleRenameTask,
    fieldName: 'name',
    onUpdateSprint,
    handlerName: 'handleCrossSprintRename',
  }),

  handleCrossSprintIconChange: createFieldHandler({
    findSprintForTask,
    localActionHandler: actions.handleIconChange,
    fieldName: 'icon',
    onUpdateSprint,
    handlerName: 'handleCrossSprintIconChange',
  }),

  handleCrossSprintPriorityChange: createFieldHandler({
    findSprintForTask,
    localActionHandler: actions.handlePriorityChange,
    fieldName: 'priority',
    onUpdateSprint,
    handlerName: 'handleCrossSprintPriorityChange',
  }),

  handleCrossSprintDifficultyChange: createFieldHandler({
    findSprintForTask,
    localActionHandler: actions.handleDifficultyChange,
    fieldName: 'difficulty',
    onUpdateSprint,
    handlerName: 'handleCrossSprintDifficultyChange',
  }),

  handleCrossSprintDueDateChange: createFieldHandler({
    findSprintForTask,
    localActionHandler: actions.handleDueDateChange,
    fieldName: 'dueDate',
    onUpdateSprint,
    handlerName: 'handleCrossSprintDueDateChange',
  }),

  handleCrossSprintStatusChange: (taskId, newStatus) => {
    const { sprint: targetSprint, isCurrentSprint } = findSprintForTask(taskId);

    if (!targetSprint) {
      console.warn(`${LOG_PREFIX} handleCrossSprintStatusChange: Task not found in any sprint:`, taskId);
      return;
    }

    if (isCurrentSprint) {
      modals.handleStatusChangeWithModal(taskId, newStatus);
    } else {
      const updatedSprint = {
        ...targetSprint,
        tasks: targetSprint.tasks.map(t =>
          t.id === taskId
            ? { ...t, status: newStatus, updatedAt: new Date().toISOString() }
            : t
        ),
      };
      onUpdateSprint?.(updatedSprint);
    }
  },

  handleCrossSprintDecisionAnswerChange: (taskId, answer) => {
    const { sprint: targetSprint, isCurrentSprint } = findSprintForTask(taskId);

    if (!targetSprint) {
      console.warn(`${LOG_PREFIX} handleCrossSprintDecisionAnswerChange: Task not found in any sprint:`, taskId);
      return;
    }

    if (isCurrentSprint) {
      actions.handleDecisionAnswerChange(taskId, answer);
    } else {
      // For nested sprints, update the task and set appropriate status
      const statuses = targetSprint.statuses || [];
      const completedStatus = statuses.find(s => s.category === 'completed')?.id || 'completed';
      const notStartedStatus = statuses.find(s => s.category === 'not_started')?.id || 'pending';
      
      const updatedSprint = {
        ...targetSprint,
        tasks: targetSprint.tasks.map(t => {
          if (t.id !== taskId) return t;
          
          const now = new Date().toISOString();
          if (answer) {
            // Decision made - set to completed
            return { 
              ...t, 
              answer, 
              status: completedStatus,
              completedAt: now,
              updatedAt: now,
            };
          } else {
            // Decision cleared - revert to not started
            return { 
              ...t, 
              answer: null, 
              status: notStartedStatus,
              completedAt: null,
              startedAt: null,
              updatedAt: now,
            };
          }
        }),
      };
      onUpdateSprint?.(updatedSprint);
    }
  },

  handleCrossSprintDecisionOptionsChange: (taskId, options) => {
    const { sprint: targetSprint, isCurrentSprint } = findSprintForTask(taskId);

    if (!targetSprint) {
      console.warn(`${LOG_PREFIX} handleCrossSprintDecisionOptionsChange: Task not found in any sprint:`, taskId);
      return;
    }

    if (isCurrentSprint) {
      actions.handleDecisionOptionsChange(taskId, options);
    } else {
      const updatedSprint = {
        ...targetSprint,
        tasks: targetSprint.tasks.map(t =>
          t.id === taskId
            ? { ...t, options, updatedAt: new Date().toISOString() }
            : t
        ),
      };
      onUpdateSprint?.(updatedSprint);
    }
  },
});
